import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Clock, Zap, Sun, Moon, Check } from 'lucide-react';
import axios from 'axios';
import logger from '@/utils/logger';

const API = '/api';

const PRESETS = [
  { id: 'early_bird', label: 'Early Bird', desc: '7am / 11:30am / 5:30pm', icon: Sun },
  { id: 'standard', label: 'Standard', desc: '8am / 12pm / 6pm', icon: Clock },
  { id: 'late_schedule', label: 'Late Schedule', desc: '10am / 1:30pm / 7:30pm', icon: Moon },
];

/**
 * Meal Schedule configuration.
 * Use as standalone settings panel or as an onboarding step.
 * Props:
 *   mode: 'settings' | 'onboarding'
 *   onComplete: callback when user finishes (onboarding mode)
 */
export function MealScheduleSettings({ mode = 'settings', onComplete }) {
  const { authHeaders } = useAuth();
  const [schedule, setSchedule] = useState(null);
  const [activePreset, setActivePreset] = useState('standard');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSchedule();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSchedule = async () => {
    try {
      const res = await axios.get(`${API}/reminders/schedule`, authHeaders());
      setSchedule(res.data.schedule);
    } catch {
      setSchedule({
        breakfast_enabled: true, breakfast_time: '08:00',
        lunch_enabled: true, lunch_time: '12:00',
        dinner_enabled: true, dinner_time: '18:00',
        snack1_enabled: false, snack1_time: '10:30',
        snack2_enabled: false, snack2_time: '15:00',
      });
    }
  };

  const applyPreset = async (presetId) => {
    setActivePreset(presetId);
    try {
      const res = await axios.put(`${API}/reminders/schedule/preset/${presetId}`, {}, authHeaders());
      setSchedule(res.data.schedule);
    } catch (err) {
      logger.error('Preset failed:', err);
    }
  };

  const updateField = (field, value) => {
    setSchedule(prev => ({ ...prev, [field]: value }));
    setActivePreset('custom');
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/reminders/schedule`, schedule, authHeaders());
    } catch (err) {
      logger.error('Save failed:', err);
    }
    setSaving(false);
    if (onComplete) onComplete();
  };

  if (!schedule) return null;

  const meals = [
    { key: 'breakfast', label: 'Breakfast', color: '#E5A84B' },
    { key: 'lunch', label: 'Lunch', color: '#6B9E8A' },
    { key: 'dinner', label: 'Dinner', color: '#4A90D9' },
    { key: 'snack1', label: 'Morning Snack', color: '#C67A5C' },
    { key: 'snack2', label: 'Afternoon Snack', color: '#C67A5C' },
  ];

  return (
    <div data-testid="meal-schedule-settings">
      {mode === 'onboarding' && (
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold text-[#1A3A4A] dark:text-[#F0EDE8] mb-1">Meal Schedule</h3>
          <p className="text-sm text-[#7A7A72] dark:text-[#A1A1AA]">
            We'll remind you when it's time to eat — with personalized meal suggestions.
          </p>
        </div>
      )}

      {/* Presets */}
      <div className="space-y-2 mb-6">
        <p className="text-xs font-medium text-[#7A7A72] dark:text-[#A1A1AA] uppercase tracking-wider">Quick Set</p>
        <div className="grid grid-cols-3 gap-2">
          {PRESETS.map(p => (
            <button
              key={p.id}
              data-testid={`preset-${p.id}`}
              onClick={() => applyPreset(p.id)}
              className={`p-3 rounded-xl text-center transition-all border ${
                activePreset === p.id
                  ? 'bg-[#1A3A4A] dark:bg-[#6B9E8A] text-white border-transparent'
                  : 'backdrop-blur-xl bg-white/70 dark:bg-[#1E2A32]/70 text-[#1A3A4A] dark:text-[#F0EDE8] border-white/40 dark:border-white/5'
              }`}
            >
              <p.icon className={`w-5 h-5 mx-auto mb-1 ${activePreset === p.id ? 'text-white' : 'text-[#7A7A72]'}`} />
              <div className="text-xs font-semibold">{p.label}</div>
              <div className={`text-[10px] mt-0.5 ${activePreset === p.id ? 'text-white/70' : 'text-[#7A7A72]'}`}>{p.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Individual meal times */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-[#7A7A72] dark:text-[#A1A1AA] uppercase tracking-wider">Meal Times</p>
        {meals.map(m => (
          <div
            key={m.key}
            className="flex items-center gap-3 p-3 rounded-xl backdrop-blur-xl bg-white/70 dark:bg-[#1E2A32]/70 border border-white/40 dark:border-white/5"
          >
            <button
              data-testid={`toggle-${m.key}`}
              onClick={() => updateField(`${m.key}_enabled`, !schedule[`${m.key}_enabled`])}
              className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                schedule[`${m.key}_enabled`]
                  ? 'text-white'
                  : 'border-2 border-[#7A7A72]/30'
              }`}
              style={schedule[`${m.key}_enabled`] ? { background: m.color } : {}}
            >
              {schedule[`${m.key}_enabled`] && <Check className="w-3.5 h-3.5" />}
            </button>
            <span className={`flex-1 text-sm font-medium ${
              schedule[`${m.key}_enabled`]
                ? 'text-[#1A3A4A] dark:text-[#F0EDE8]'
                : 'text-[#7A7A72]/50'
            }`}>
              {m.label}
            </span>
            <input
              data-testid={`time-${m.key}`}
              type="time"
              value={schedule[`${m.key}_time`] || '12:00'}
              onChange={e => updateField(`${m.key}_time`, e.target.value)}
              disabled={!schedule[`${m.key}_enabled`]}
              className="text-sm font-mono bg-transparent border-0 text-[#1A3A4A] dark:text-[#F0EDE8] disabled:opacity-30 w-20 text-right"
            />
          </div>
        ))}
      </div>

      {/* Save */}
      {mode === 'settings' && (
        <Button
          data-testid="save-schedule"
          onClick={saveSchedule}
          disabled={saving}
          className="w-full h-12 mt-6 rounded-xl bg-[#1A3A4A] dark:bg-[#6B9E8A] text-white font-semibold"
        >
          {saving ? 'Saving...' : 'Save Schedule'}
        </Button>
      )}

      {mode === 'onboarding' && (
        <Button
          data-testid="schedule-continue"
          onClick={saveSchedule}
          className="w-full h-12 mt-6 rounded-xl bg-[#1A3A4A] dark:bg-[#6B9E8A] text-white font-semibold"
        >
          Continue
        </Button>
      )}
    </div>
  );
}

export default MealScheduleSettings;
